import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Alert } from "react-bootstrap";
import * as recipeService from "@beers/services/recipeService";
import "./brew-setup-modal.scss";

const BrewSetupModal = ({ show, handleClose, session }) => {
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeStageIndex, setActiveStageIndex] = useState(0);

  const recipeId = session ? session.beer_recipe_id : null;

  useEffect(() => {
    if (show) {
      setActiveStageIndex(0);
    }
  }, [show, recipeId]);

  useEffect(() => {
    let isMounted = true;

    const fetchRecipe = async () => {
      if (!recipeId || !show) {
        return;
      }
      try {
        setLoading(true);
        setError(null);
        let data;
        try {
          data = await recipeService.getRecipeById(recipeId);
        } catch (recipeErr) {
          data = await recipeService.getSharedRecipe(recipeId);
        }
        if (isMounted) {
          setRecipe(data);
        }
      } catch (err) {
        if (isMounted) {
          setError(
            (err && err.message) ||
              "Could not load recipe details. Please try again.",
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchRecipe();

    return () => {
      isMounted = false;
    };
  }, [show, recipeId]);

  // Water calculations
  const totalWater =
    recipe && recipe.water_amount ? Number(recipe.water_amount) : 0;
  const kettleWater =
    recipe && recipe.kettle_water ? Number(recipe.kettle_water) : 0;
  const mashWater = Math.max(0, totalWater - kettleWater);

  // Hop carousel slots calculation (longest boil duration -> Slot 1)
  const rawHops = [];
  if (recipe && Array.isArray(recipe.boiling)) {
    recipe.boiling.forEach((step) => {
      if (Array.isArray(step.hops)) {
        rawHops.push(...step.hops);
      }
      if (Array.isArray(step.other_ingredients)) {
        step.other_ingredients.forEach((ing) => {
          if (ing && ing.ingredient_type !== "FERM") {
            rawHops.push(ing);
          }
        });
      }
    });
  }

  const sortedHops = [...rawHops].sort(
    (a, b) => (Number(b.duration) || 0) - (Number(a.duration) || 0),
  );

  const carouselSlots = [1, 2, 3, 4, 5, 6].map((slotNumber) => {
    const item = sortedHops[slotNumber - 1];
    return {
      slot: slotNumber,
      item: item || null,
    };
  });

  // Group grains by mash stages
  const mashStages = useMemo(() => {
    if (!recipe || !Array.isArray(recipe.mashing)) return [];
    return recipe.mashing.map((stage, idx) => {
      const stageGrains = [];
      let stageGrams = 0;

      if (stage && Array.isArray(stage.ingredient_additions)) {
        stage.ingredient_additions.forEach((addition) => {
          if (
            addition &&
            (addition.ingredient_type === "FERM" || !addition.ingredient_type)
          ) {
            const amount = Number(addition.amount) || 0;
            stageGrams += amount;
            stageGrains.push({
              name:
                addition.ingredient_name ||
                addition.name ||
                addition.fermentable_name ||
                "Grain",
              amount,
            });
          }
        });
      }

      if (stage && Array.isArray(stage.steps)) {
        stage.steps.forEach((step) => {
          if (step && Array.isArray(step.other_ingredients)) {
            step.other_ingredients.forEach((ing) => {
              if (
                ing &&
                (ing.ingredient_type === "FERM" || !ing.ingredient_type)
              ) {
                const amount = Number(ing.amount) || 0;
                stageGrams += amount;
                stageGrains.push({
                  name:
                    (ing.fermentable && ing.fermentable.name) ||
                    ing.name ||
                    "Grain",
                  amount,
                });
              }
            });
          }
        });
      }

      let title = `Mash Stage ${idx + 1}`;
      if (stage.name && !stage.name.toLowerCase().startsWith("mash stage:")) {
        title = stage.name;
      }

      const firstStep = stage.steps && stage.steps[0];
      const scheme =
        firstStep && firstStep.temperature
          ? `${firstStep.duration} min @ ${Number(firstStep.temperature).toFixed(1)}°C`
          : null;

      return {
        id: stage.id || idx,
        title,
        order: stage.order !== undefined ? stage.order : idx,
        grains: stageGrains,
        totalGrams: stageGrams,
        scheme,
      };
    });
  }, [recipe]);

  const totalGrainGrams = mashStages.reduce(
    (sum, stage) => sum + stage.totalGrams,
    0,
  );
  const totalGrainsCount = mashStages.reduce(
    (sum, stage) => sum + stage.grains.length,
    0,
  );

  const hasMultipleStages = mashStages.length > 1;
  const currentStage = mashStages[activeStageIndex] ||
    mashStages[0] || {
      title: "Mash Stage 1",
      grains: [],
      totalGrams: 0,
      scheme: null,
    };

  const beerName =
    (recipe && recipe.beer_name) ||
    (session && session.beer && session.beer.name) ||
    "MiniBrew";
  const versionName =
    (recipe && recipe.version_name) ||
    (session && session.beer_recipe_version
      ? `v${session.beer_recipe_version}`
      : "");

  return (
    <Modal
      show={show}
      onHide={handleClose}
      className="brew-setup-modal"
      bsSize="large"
    >
      <Modal.Header closeButton>
        <Modal.Title className="brew-setup-modal__title">
          Brew Setup &amp; Ingredients
        </Modal.Title>
        <div className="brew-setup-modal__subtitle">
          {beerName} {versionName && `— ${versionName}`}
        </div>
      </Modal.Header>

      <Modal.Body className="brew-setup-modal__body">
        {loading && (
          <div className="brew-setup-modal__loading">
            <span className="start-brew-loading__icon ion-load-c spin" />
            Loading recipe details...
          </div>
        )}

        {error && (
          <Alert bsStyle="danger" className="brew-setup-modal__error">
            <span className="ion-alert-circled" /> {error}
          </Alert>
        )}

        {!loading && (
          <>
            {/* Water Distribution Section */}
            <div className="brew-setup-modal__section">
              <div className="brew-setup-modal__section-title">
                <span className="icon ion-waterdrop" /> Water Distribution
              </div>
              <div className="brew-setup-modal__water-grid">
                <div className="brew-setup-modal__water-card brew-setup-modal__water-card--highlight">
                  <div className="brew-setup-modal__water-label">
                    SmartKeg Water
                  </div>
                  <div className="brew-setup-modal__water-value">
                    {kettleWater.toFixed(1)} L
                  </div>
                  <div className="brew-setup-modal__water-hint">
                    Pour directly into SmartKeg
                  </div>
                </div>

                <div className="brew-setup-modal__water-card">
                  <div className="brew-setup-modal__water-label">
                    Mash Tun Water
                  </div>
                  <div className="brew-setup-modal__water-value">
                    {mashWater.toFixed(1)} L
                  </div>
                  <div className="brew-setup-modal__water-hint">
                    Mix with grain in Mash Tun
                  </div>
                </div>

                <div className="brew-setup-modal__water-card">
                  <div className="brew-setup-modal__water-label">
                    Total Water
                  </div>
                  <div className="brew-setup-modal__water-value">
                    {totalWater.toFixed(1)} L
                  </div>
                  <div className="brew-setup-modal__water-hint">
                    Total required for batch
                  </div>
                </div>
              </div>
            </div>

            {/* Hop Carousel Slots Section */}
            <div className="brew-setup-modal__section">
              <div className="brew-setup-modal__section-title">
                <span className="icon ion-ios-circle-filled" /> Hop Carousel
                Slots
              </div>
              <div className="brew-setup-modal__slots-grid">
                {carouselSlots.map(({ slot, item }) => (
                  <div
                    key={slot}
                    className={`brew-setup-modal__slot-card ${
                      item
                        ? "brew-setup-modal__slot-card--filled"
                        : "brew-setup-modal__slot-card--empty"
                    }`}
                  >
                    <div
                      className={`brew-setup-modal__slot-badge ${
                        item ? "brew-setup-modal__slot-badge--active" : ""
                      }`}
                    >
                      {slot}
                    </div>
                    <div className="brew-setup-modal__slot-info">
                      {item ? (
                        <>
                          <div className="brew-setup-modal__slot-name">
                            {item.ingredient_name || item.name || "Hop"}
                          </div>
                          <div className="brew-setup-modal__slot-detail">
                            {item.amount || 0}g &bull; {item.duration || 0} min
                            boil
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="brew-setup-modal__slot-name">
                            (Empty)
                          </div>
                          <div className="brew-setup-modal__slot-detail">
                            No addition
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Grains & Malts Section */}
            {totalGrainsCount > 0 && (
              <div className="brew-setup-modal__section">
                <div className="brew-setup-modal__section-title">
                  <span className="icon ion-cube" /> Malt &amp; Grains
                </div>

                {hasMultipleStages && (
                  <div className="brew-setup-modal__stage-tabs" role="tablist">
                    {mashStages.map((stage, idx) => (
                      <button
                        key={idx}
                        type="button"
                        role="tab"
                        aria-selected={activeStageIndex === idx}
                        className={`brew-setup-modal__stage-tab ${
                          activeStageIndex === idx
                            ? "brew-setup-modal__stage-tab--active"
                            : ""
                        }`}
                        onClick={() => setActiveStageIndex(idx)}
                      >
                        <span className="brew-setup-modal__stage-tab-title">
                          {stage.title}
                        </span>
                        {stage.scheme && (
                          <span className="brew-setup-modal__stage-scheme">
                            {stage.scheme}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                <div className="brew-setup-modal__grain-list">
                  {currentStage.grains.map((grain, idx) => (
                    <div key={idx} className="brew-setup-modal__grain-item">
                      <span className="brew-setup-modal__grain-name">
                        {grain.name}
                      </span>
                      <span className="brew-setup-modal__grain-amount">
                        {grain.amount >= 1000
                          ? `${(grain.amount / 1000).toFixed(2)} kg`
                          : `${grain.amount} g`}
                      </span>
                    </div>
                  ))}

                  {/* Subtotal for current stage when multi-stage */}
                  {hasMultipleStages && (
                    <div className="brew-setup-modal__grain-subtotal">
                      <span>{`${currentStage.title} Subtotal`}</span>
                      <span>
                        {currentStage.totalGrams >= 1000
                          ? `${(currentStage.totalGrams / 1000).toFixed(2)} kg`
                          : `${currentStage.totalGrams} g`}
                      </span>
                    </div>
                  )}

                  <div className="brew-setup-modal__grain-total">
                    <span>
                      {hasMultipleStages
                        ? "Total Grain Bill (All Stages)"
                        : "Total Grain Bill"}
                    </span>
                    <span>
                      {totalGrainGrams >= 1000
                        ? `${(totalGrainGrams / 1000).toFixed(2)} kg`
                        : `${totalGrainGrams} g`}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button onClick={handleClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BrewSetupModal;
